import React, { useEffect, useState } from "react";

// One numbered section of a long legal page. LegalLayout reads `id` and
// `title` off each of these to build the contents list, so a section only
// needs adding in one place.
export function LegalSection({ id, number, title, children }) {
  return (
    <section id={id} className="legal-section">
      <h2 className="legal-section-title">
        {number && <span className="legal-section-number">{number}</span>}
        {title}
      </h2>
      {children}
    </section>
  );
}

// Long-form page layout: sticky contents on the left, the document on the
// right. Highlights whichever section is currently being read.
export default function LegalLayout({ intro, children }) {
  const sections = React.Children.toArray(children)
    .filter((child) => child?.props?.id)
    .map((child, index) => ({
      id: child.props.id,
      title: child.props.title,
      number: index + 1,
    }));

  const [activeId, setActiveId] = useState(sections[0]?.id);

  useEffect(() => {
    const targets = sections
      .map(({ id }) => document.getElementById(id))
      .filter(Boolean);
    if (!targets.length || typeof IntersectionObserver === "undefined") {
      return undefined;
    }

    // A section counts as "current" once its top passes the upper third of
    // the viewport.
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) setActiveId(visible[0].target.id);
      },
      { rootMargin: "-20% 0px -65% 0px" }
    );

    targets.forEach((target) => observer.observe(target));
    return () => observer.disconnect();
    // Section ids are static for a given page.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const contents = (
    <ol className="legal-toc-list">
      {sections.map(({ id, title, number }) => (
        <li key={id}>
          <a
            href={`#${id}`}
            className={`legal-toc-link ${activeId === id ? "active" : ""}`}
            aria-current={activeId === id ? "true" : undefined}
          >
            <span className="legal-toc-number">{number}</span>
            {title}
          </a>
        </li>
      ))}
    </ol>
  );

  return (
    <div className="legal-layout">
      <aside className="legal-toc">
        {/* Desktop: always open. Mobile: collapses to save scrolling. */}
        <p className="legal-toc-title">On this page</p>
        <nav aria-label="On this page" className="legal-toc-desktop">
          {contents}
        </nav>
        <details className="legal-toc-mobile">
          <summary>On this page</summary>
          <nav aria-label="On this page">{contents}</nav>
        </details>
      </aside>

      <article className="legal-doc">
        {intro}
        {React.Children.map(children, (child) =>
          child?.props?.id
            ? React.cloneElement(child, {
                number: sections.findIndex((s) => s.id === child.props.id) + 1,
              })
            : child
        )}
      </article>
    </div>
  );
}
